package com.neurobs.ccabserver.transcriptreview.states

import com.neurobs.ccab.CCABServer
import com.neurobs.ccab.db.{LogfileData, ReviewedTestTranscriptData, TestTranscriptData}
import com.neurobs.errors._
import com.neurobs.ccab.casr.CasrDatabaseJSON.TestTranscriptJSON
import com.neurobs.http
import com.neurobs.js.util.ServerResponseUtil

import scala.concurrent.Future
import org.scalajs.dom.{AudioBuffer, AudioContext}

import scala.concurrent.ExecutionContext.Implicits.global


case class SpeechAudioFileMetadata
(
	code: String
)

case class SpeechAudioFileDataContents
(
	metadata: SpeechAudioFileMetadata,
	contents: AudioBuffer
)

/** Immutable data regarding a test transcript from the server required for transcript review */
case class TestTranscriptServerData
(
	selection: TestTranscriptSelection,
	transcript: TestTranscriptData,
	audioFiles: Seq[SpeechAudioFileDataContents],
	reviewedTranscripts: Seq[ReviewedTestTranscriptData],
	logfileName: String
)

object TestTranscriptServerData {

	/** Retrieves the audio contents for the requested speech audio files */
	private def getAudioContents(testRunId: Long, wavFileCodes: Seq[String])(implicit server: CCABServer, audioContext: AudioContext): FutureResult[Seq[SpeechAudioFileDataContents]] = {
		val futures: Seq[Future[Result[(SpeechAudioFileMetadata, http.BodyData)]]] =
			wavFileCodes.map(code => server.getSpeechAudioFileFile(testRunId, code)._2.map(_.map(
				b => (SpeechAudioFileMetadata(code = code), b)))
			)
		val buffers: Seq[Future[Result[SpeechAudioFileDataContents]]] = {
			futures map { f =>
				f flatMap {
					case Left(e) => Future.successful(Left(e))
					case Right(f) => ServerResponseUtil.decodeAudioData(Right(f._2)).map(_.map(b => SpeechAudioFileDataContents(f._1, b)))
				}
			}
		}
		Future.sequence(buffers).map(sequence(_))
	}

	/** Retrieves wav file names from transcript */
	private def getWavFileCodes(testTranscript: TestTranscriptData): FutureResult[Seq[String]] = {
		testTranscript.transcript match {
			case Some(transcript) =>
				Future(TestTranscriptJSON.fromJsonString(transcript).flatMap(transcriptJSON =>
					Right(transcriptJSON.recordings().map(_.code()))
				))
			case None => FutureResult[Seq[String]](Future(Left(s"No transcript for test_transcript.id ${testTranscript.id}")))
		}
	}

	/** Retrieves all necessary transcript data from the server for the given selection */
	def apply(selection: TestTranscriptSelection)(implicit server: CCABServer, audioContext: AudioContext): FutureResult[TestTranscriptServerData] = {
		val transcriptFut = FutureResult(server.getTestTranscriptEntry(id = selection.transcript.testTranscriptId)._2)
		val reviewedFut: FutureResult[Seq[ReviewedTestTranscriptData]] = {
			sequence(selection.reviewed.map(t => FutureResult(server.getReviewedTestTranscript(t.reviewedTestTranscriptId)._2)))
		}
		val logfileFut = FutureResult[Seq[LogfileData]](server.getLogfiles(selection.transcript.testRunId)._2)
		for {
			transcript <- transcriptFut
			reviewedTranscripts <- reviewedFut
			wavCodes <- getWavFileCodes(transcript)
			audioFiles <- getAudioContents(selection.transcript.testRunId, wavCodes)
			logfile <- logfileFut
		} yield {
			TestTranscriptServerData(selection, transcript, audioFiles, reviewedTranscripts, logfile.head.origFilename)
		}
	}
}